/**
 * ListingDescription Component
 *
 * Renders the listing description with an instant, client-side
 * view-original/translate toggle (Unicorn Standard): all three locale columns
 * are shipped on the wire, so switching between the original and the translated
 * description needs no server round-trip.
 *
 * Behavior (the Rust SSOT `domain_core::translation_toggle` — no longer
 * hand-composed here):
 *   - `auto_translate` ON  → show the viewer-locale translation.
 *   - `auto_translate` OFF → show the lister's original.
 *   - The notice + toggle appear ONLY when the viewer's locale differs from the
 *     origin locale (otherwise the viewer is already reading the original).
 *
 * `auto_translate` is the user preference (cookie-backed `usePreferences`,
 * SSR-safe); the toggle overrides it for this listing view only.
 */
import { usePreferences } from '@/shared/hooks/usePreferences';
import { useTranslations } from '@/shared/i18n';
import { pickLocalizedExact, type LocalizedStringData } from '@letsgethome/lib/i18n';
import { translationDisplay } from '@letsgethome/lib/wasm';
import { isLocale, plainText, type Locale } from '@letsgethome/types/branded';
import { UnstyledButton } from '@letsgethome/ui/unstyled-button';
import { useState } from 'react';

type Props = {
  /** All three description columns (lossless) — the client resolves locally. */
  description: LocalizedStringData;
  /** Origin locale the description was authored in. */
  descriptionLocale: Locale;
  /** The locale the viewer is browsing in. */
  viewerLocale: Locale;
};

export function ListingDescription({ description, descriptionLocale, viewerLocale }: Props) {
  const t = useTranslations('listings.detail');
  const { preferences } = usePreferences();
  // Per-view override of the global auto-translate preference (null = follow it).
  const [overrideTranslated, setOverrideTranslated] = useState<boolean | null>(null);

  // The toggle decision is the Rust SSOT
  // (`domain_core::translation_toggle::translation_display`, via
  // `domain-wasm`) — shared verbatim with both mobile listing screens. The
  // component keeps only the override STATE and the words.
  const display = translationDisplay(
    viewerLocale,
    descriptionLocale,
    preferences.autoTranslate,
    overrideTranslated
  );
  const { isForeign, showTranslated } = display;
  // The SSOT returns one of the BCP-47 tags it was handed; `isLocale` is
  // the sanctioned string → branded-Locale narrowing (the fallback arm is
  // unreachable).
  const localeToShow = isLocale(display.localeToShow) ? display.localeToShow : viewerLocale;
  const text = pickLocalizedExact(description, localeToShow);

  if (!text) return null;

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">{t('description')}</h3>

      {isForeign && (
        <div className="flex items-center justify-between gap-3 rounded-md border border-border bg-muted px-3 py-2">
          <span className="text-sm text-muted-foreground">
            {showTranslated ? t('translatedNotice') : t('originalNotice')}
          </span>
          <UnstyledButton
            type="button"
            className="text-sm font-medium text-primary hover:opacity-80"
            onClick={() => setOverrideTranslated(!showTranslated)}
          >
            {showTranslated ? t('showOriginal') : t('showTranslation')}
          </UnstyledButton>
        </div>
      )}

      <p className="whitespace-pre-wrap text-muted-foreground leading-relaxed">{plainText(text)}</p>
    </div>
  );
}
